import React, { useState, useContext, useEffect } from 'react';
import { ToastContext } from '../App';

const today = new Date().toISOString().split('T')[0];

export default function BookingModal({ car, onClose, onSuccess }) {
  const showToast = useContext(ToastContext);
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [form, setForm]       = useState({ customerName:'', email:'', phone:'', pickupDate:'', returnDate:'' });
  const [errors, setErrors]   = useState({});

  const set = (k, v) => { setForm(f => ({...f,[k]:v})); setErrors(e => ({...e,[k]:''})); };

  const days = form.pickupDate && form.returnDate
    ? Math.max(0, Math.ceil((new Date(form.returnDate) - new Date(form.pickupDate)) / 86400000))
    : 0;
  const total = days * (car?.price || 0);

  const validate = () => {
    const e = {};
    if (!form.customerName.trim()) e.customerName = 'Required';
    if (!form.phone.trim())        e.phone        = 'Required';
    if (!form.email.trim())        e.email        = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    if (!form.pickupDate)          e.pickupDate   = 'Required';
    if (!form.returnDate)          e.returnDate   = 'Required';
    else if (days < 1)             e.returnDate   = 'Must be after pickup';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res  = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, carId: car.id }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Booking failed', 'error'); setLoading(false); return; }
      setBooking(data);
      setStep(2);
      if (onSuccess) onSuccess();
    } catch {
      showToast('Could not connect to server. Please try again.', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.72)',
      backdropFilter:'blur(6px)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'16px', animation:'bm_fade 0.18s ease',
    }}>
      <style>{`
        @keyframes bm_fade  { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        @keyframes bm_up    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .bm-input { width:100%; padding:11px 14px; border:1.5px solid #E5E7EB; border-radius:10px;
          font-size:14px; font-family:inherit; color:#111827; background:#FAFAFA; outline:none;
          transition:border-color 0.15s,background 0.15s; box-sizing:border-box; }
        .bm-input:focus { border-color:#C41230; background:#fff; }
        .bm-input.err   { border-color:#DC2626; background:#FFF5F5; }
        .bm-lbl { display:block; font-size:11px; font-weight:700; color:#6B7280;
          text-transform:uppercase; letter-spacing:0.07em; margin-bottom:6px; }
        .bm-err { color:#DC2626; font-size:11px; margin-top:4px; }
        .bm-primary { background:#C41230; color:#fff; border:none; border-radius:12px;
          padding:14px 24px; font-size:14px; font-weight:700; cursor:pointer;
          font-family:inherit; transition:all 0.15s; }
        .bm-primary:hover:not(:disabled) { background:#A50F27; transform:translateY(-1px); box-shadow:0 6px 20px rgba(196,18,48,0.35); }
        .bm-primary:disabled { opacity:0.6; cursor:not-allowed; }
        .bm-ghost { background:transparent; color:#6B7280; border:1.5px solid #E5E7EB;
          border-radius:12px; padding:13px 20px; font-size:14px; font-weight:600;
          cursor:pointer; font-family:inherit; transition:all 0.15s; }
        .bm-ghost:hover { border-color:#C41230; color:#C41230; } @media (max-width: 480px) {
  .bm-grid-2col { grid-template-columns: 1fr !important; }
}
      `}</style>

      <div style={{
        background:'#fff', borderRadius:20, width:'100%', maxWidth:500,
        maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column',
        boxShadow:'0 32px 80px rgba(0,0,0,0.35)', animation:'bm_up 0.22s ease',
      }}>

        {step === 2 ? (
          <div style={{ padding:'40px 32px', textAlign:'center', animation:'bm_up 0.2s ease' }}>
            <div style={{ width:64, height:64, background:'#DCFCE7', borderRadius:'50%',
              display:'flex', alignItems:'center', justifyContent:'center',
              margin:'0 auto 20px', fontSize:28 }}>✅</div>
            <div style={{ fontSize:22, fontWeight:800, color:'#111827', marginBottom:6 }}>Booking Confirmed!</div>
            <div style={{ fontSize:14, color:'#6B7280', marginBottom:28 }}>
              Thank you, <strong>{booking?.customerName?.split(' ')[0]}</strong>. A confirmation email is on its way.
            </div>
            <div style={{ background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:14, padding:'18px 20px', marginBottom:28, textAlign:'left' }}>
              {[['Booking ID',`#${booking?.id}`],['Vehicle',car?.name],['Pickup',booking?.pickupDate],['Return',booking?.returnDate],['Duration',`${days} day${days!==1?'s':''}`],['Total',`€${total}`]].map(([l,v])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:14 }}>
                  <span style={{ color:'#9CA3AF', fontWeight:500 }}>{l}</span>
                  <span style={{ fontWeight:700, color:l==='Total'?'#C41230':'#111827' }}>{v}</span>
                </div>
              ))}
            </div>
            <button className="bm-primary" style={{ width:'100%' }} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'20px 24px 16px', borderBottom:'1px solid #F3F4F6', flexShrink:0 }}>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Reserve Your Car</div>
                <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}>Fill in your details to confirm</div>
              </div>
              <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8,
                width:32, height:32, cursor:'pointer', display:'flex', alignItems:'center',
                justifyContent:'center', color:'#6B7280', fontSize:15, transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='#FEE2E2';e.currentTarget.style.color='#C41230';}}
                onMouseLeave={e=>{e.currentTarget.style.background='#F3F4F6';e.currentTarget.style.color='#6B7280';}}>✕</button>
            </div>

            {/* Car card */}
            <div style={{ margin:'16px 24px 0', background:'#0C0C0C', borderRadius:14,
              display:'flex', alignItems:'center', gap:16, padding:'14px 18px', flexShrink:0 }}>
              {car?.image
                ? <img src={car.image} alt={car.name} style={{ width:88, height:56, objectFit:'cover', borderRadius:8, flexShrink:0, border:'1px solid rgba(255,255,255,0.08)' }}/>
                : <div style={{ width:88, height:56, background:'#1C1C1C', borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>🚗</div>
              }
              <div style={{ flex:1 }}>
                <div style={{ fontSize:16, fontWeight:800, color:'#fff' }}>{car?.name}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{car?.year} · {car?.category}</div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:24, fontWeight:900, color:'#C41230', lineHeight:1 }}>€{car?.price}</div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', fontWeight:600, letterSpacing:'0.06em' }}>/DAY</div>
              </div>
            </div>

            {/* Form */}
            <div style={{ padding:'20px 24px', overflowY:'auto', flex:1 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }} className="bm-grid-2col">
                <div>
                  <label className="bm-lbl">Full Name <span style={{color:'#C41230'}}>*</span></label>
                  <input className={`bm-input${errors.customerName?' err':''}`} placeholder="John Smith"
                    value={form.customerName} onChange={e=>set('customerName',e.target.value)}/>
                  {errors.customerName && <div className="bm-err">{errors.customerName}</div>}
                </div>
                <div>
                  <label className="bm-lbl">Phone <span style={{color:'#C41230'}}>*</span></label>
                  <input className={`bm-input${errors.phone?' err':''}`} placeholder="+355 69 123 4567"
                    value={form.phone} onChange={e=>set('phone',e.target.value)}/>
                  {errors.phone && <div className="bm-err">{errors.phone}</div>}
                </div>
              </div>

              <div style={{ marginBottom:12 }}>
                <label className="bm-lbl">Email <span style={{color:'#C41230'}}>*</span></label>
                <input className={`bm-input${errors.email?' err':''}`} type="email" placeholder="john@example.com"
                  value={form.email} onChange={e=>set('email',e.target.value)}/>
                {errors.email && <div className="bm-err">{errors.email}</div>}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }} className="bm-grid-2col">
                <div>
                  <label className="bm-lbl">Pickup Date <span style={{color:'#C41230'}}>*</span></label>
                  <input className={`bm-input${errors.pickupDate?' err':''}`} type="date" min={today}
                    value={form.pickupDate} onChange={e=>set('pickupDate',e.target.value)}/>
                  {errors.pickupDate && <div className="bm-err">{errors.pickupDate}</div>}
                </div>
                <div>
                  <label className="bm-lbl">Return Date <span style={{color:'#C41230'}}>*</span></label>
                  <input className={`bm-input${errors.returnDate?' err':''}`} type="date" min={form.pickupDate||today}
                    value={form.returnDate} onChange={e=>set('returnDate',e.target.value)}/>
                  {errors.returnDate && <div className="bm-err">{errors.returnDate}</div>}
                </div>
              </div>

              {days > 0 && (
                <div style={{ background:'#0C0C0C', borderRadius:12, padding:'14px 18px',
                  display:'flex', justifyContent:'space-between', alignItems:'center', animation:'bm_fade 0.2s ease' }}>
                  <div>
                    <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                      {days} Day{days!==1?'s':''} · €{car?.price}/day
                    </div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.25)', marginTop:3 }}>
                      {form.pickupDate} → {form.returnDate}
                    </div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em' }}>Total</div>
                    <div style={{ fontSize:28, fontWeight:900, color:'#C41230', lineHeight:1.1 }}>€{total}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding:'14px 24px 20px', borderTop:'1px solid #F3F4F6',
              display:'flex', gap:10, flexShrink:0 }}>
              <button className="bm-ghost" onClick={onClose} style={{flex:1}}>Cancel</button>
              <button className="bm-primary" onClick={handleSubmit} disabled={loading} style={{flex:2}}>
                {loading ? 'Processing…' : `Confirm Booking${total > 0 ? ` · €${total}` : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}